import { AntDesign } from "@expo/vector-icons";
import { useFocusEffect } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatListSearchable,
  Page,
  TextButton,
} from "@towersystems/roam-common-native";
import { Customer } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { Spacings } from "react-native-ui-lib";
import { CustomerResourceListItem } from "../../../../components/CustomerResourceListItem/CustomerResourceListItem";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { CustomerRootParamList } from "../../../../types";
import { useTranslation } from "../../../../utilities/i18n";
import { customerFilterValuesToApiInput } from "../../common";
import { CustomerFilterValues } from "../../types";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { useScheduler } from "../../../../utilities/scheduler";
import { SearchContext, useSearch } from "../../../../utilities/search";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import { QUERY_CUSTOMER_LIST } from "../../utils/customer";

export type CustomersListScreenProps = StackScreenProps<
  CustomerRootParamList,
  "CustomerListScreen"
>;

export const CustomersListScreen = ({
  navigation,
}: CustomersListScreenProps) => {
  const { t } = useTranslation("customer");
  const search = useSearch();
  const { schedule } = useScheduler(500);
  const transitionEnded = useHasTransitionEnded(navigation);

  const { filterState, watch } = useListFilterContext<CustomerFilterValues>();
  const { activeFiltersCount } = filterState;

  const watchedFilterValues = watch();

  const loadMoreList = useLoadMoreList<Customer>({
    initialLoad: false,
    query: QUERY_CUSTOMER_LIST,
    resultKey: "customers",
    sort: { updatedAt: "DESC" },
    variableFormatter: (variables) => {
      return {
        options: {
          ...variables,
        },
      };
    },
  });

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label="New Customer"
          isAction
          onPress={() => navigation.navigate("CreateCustomerScreen")}
          size="compact"
        />
      ),
    });
  }, []);

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("CustomerListFilterRootScreen", {
                  screen: "CustomerListFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount]),
  );

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
          ...customerFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset,
    );
  };

  useFocusEffect(
    React.useCallback(() => {
      handleLoadMore(true);
    }, [watchedFilterValues]),
  );

  const items: Customer[] = loadMoreList.items || [];

  if (
    (loadMoreList.loading &&
      loadMoreList.items.length === 0 &&
      !search.query.length) ||
    false === transitionEnded
  ) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />
      </Page>
    );
  }

  return (
    <SearchContext.Provider value={search}>
      <Page scrollable={false} horizontalPadding={0} fullWidth>
        <FlatListSearchable
          contentContainerStyle={{
            maxWidth: DEFAULT_PAGE_WIDTH,
            width: "100%",
            alignSelf: "center",
            paddingHorizontal: Spacings.s5,
            paddingTop: Spacings.s5,
          }}
          placeholder="Search..."
          onChange={(query) => search.setQuery(query)}
          value={search.query}
          ItemSeparatorComponent={Divider}
          renderItem={({ item }: { item: Customer }) => (
            <CustomerResourceListItem
              customer={item}
              action={() =>
                navigation.navigate("EditCustomerScreen", {
                  customerId: item.id,
                })
              }
            />
          )}
          refreshing={loadMoreList.loading}
          data={items}
          scrollEnabled={true}
          onEndReachedThreshold={0.3}
          onEndReached={() => {
            items.length >= 1 && handleLoadMore(false);
          }}
        />
      </Page>
    </SearchContext.Provider>
  );
};
